import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Download, Filter, Loader2, Search, Users,
  BarChart3, TrendingUp, PieChart,
} from "lucide-react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <Card className="bg-white border-slate-200">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <p className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500">{label}</p>
          <Icon className="h-4 w-4 text-primary/70" />
        </div>
        <p className="mt-2 font-heading text-3xl font-semibold text-slate-900">{value}</p>
        {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      </CardContent>
    </Card>
  );
}

export default function AdminAssessments() {
  const { user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = {};
    if (email.trim()) p.email = email.trim();
    if (start) p.start = new Date(start).toISOString();
    if (end) {
      // include the whole end-day
      const d = new Date(end);
      d.setHours(23, 59, 59, 999);
      p.end = d.toISOString();
    }
    return p;
  }, [email, start, end]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: st }] = await Promise.all([
        api.get("/admin/assessments", { params }),
        api.get("/admin/assessments/stats", { params }),
      ]);
      setItems(list.assessments || []);
      setStats(st);
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not load assessments.");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const clearFilters = () => { setEmail(""); setStart(""); setEnd(""); };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("ccms_token");
      const qs = new URLSearchParams(params).toString();
      const url = `${API_BASE}/admin/assessments/export.csv${qs ? "?" + qs : ""}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const link = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = `cgi_assessments_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      toast.error("Could not download CSV.");
    }
  };

  const dist = stats?.quadrant_distribution || {};
  const distTotal = Object.values(dist).reduce((a, b) => a + b, 0) || 1;
  const topQuadrant = Object.entries(dist)[0]?.[0] || "—";

  return (
    <div className="min-h-screen bg-clinical" data-testid="admin-assessments-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <Button asChild variant="outline" className="gap-2">
            <Link to="/" data-testid="admin-assessments-back">
              <ArrowLeft className="h-4 w-4" /> Back to dashboard
            </Link>
          </Button>
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-primary">
            Admin · Completed assessments
          </div>
        </div>

        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900">
            Completed assessments
          </h1>
          <p className="mt-2 text-slate-600 text-sm">
            Every assessment submitted through the app. Click a row to open the report the user saw.
          </p>
        </div>

        {/* Stats panel */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="stats-panel">
          <StatCard icon={BarChart3} label="Total completions" value={stats?.total ?? "—"} />
          <StatCard icon={Users} label="Unique users" value={stats?.unique_users ?? "—"} />
          <StatCard icon={TrendingUp} label="Avg overall score" value={`${stats?.avg_overall_percent ?? 0}%`} />
          <StatCard icon={PieChart} label="Most common quadrant" value={topQuadrant} hint={`${dist[topQuadrant] || 0} of ${stats?.total || 0}`} />
        </div>

        {/* Subscale averages */}
        <Card className="bg-white border-slate-200 mb-6">
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-3">Average subscale scores</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {["empathy", "calm", "reactivity", "engagement"].map((k) => (
                <div key={k}>
                  <p className="text-xs text-slate-500 capitalize">{k}</p>
                  <p className="font-heading text-2xl font-semibold text-slate-900">{stats?.avg_subscales?.[k] ?? 0}<span className="text-sm text-slate-500">%</span></p>
                </div>
              ))}
            </div>
            {Object.keys(dist).length > 0 && (
              <>
                <p className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mt-6 mb-2">Quadrant distribution</p>
                <div className="space-y-1.5">
                  {Object.entries(dist).map(([name, count]) => {
                    const pct = Math.round((count / distTotal) * 100);
                    return (
                      <div key={name} className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between text-xs mb-0.5">
                            <span className="text-slate-700 truncate">{name}</span>
                            <span className="text-slate-500">{count} · {pct}%</span>
                          </div>
                          <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="bg-white border-slate-200 mb-6" data-testid="filters-panel">
          <CardContent className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <Search className="h-3.5 w-3.5" /> Search by email
                </label>
                <Input
                  data-testid="filter-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g. mike@"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <Filter className="h-3.5 w-3.5" /> From
                </label>
                <Input type="date" data-testid="filter-start" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">To</label>
                <Input type="date" data-testid="filter-end" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" />
              </div>
            </div>
            <div className="flex justify-between items-center mt-4 flex-wrap gap-2">
              <Button variant="ghost" onClick={clearFilters} data-testid="filter-clear">
                Clear filters
              </Button>
              <Button onClick={downloadCsv} data-testid="csv-download" className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
                <Download className="h-4 w-4" /> Download CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500" data-testid="assessments-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading assessments…
          </div>
        ) : items.length === 0 ? (
          <Card className="border-dashed border-slate-300 bg-white/60">
            <CardContent className="p-10 text-center" data-testid="assessments-empty">
              <p className="font-heading text-lg text-slate-800">No matching assessments</p>
              <p className="mt-1 text-slate-500 text-sm">Try clearing filters or expanding the date range.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="rounded-lg overflow-hidden border border-slate-200 bg-white" data-testid="assessments-table">
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider font-semibold text-slate-500">
              <div className="col-span-4">User</div>
              <div className="col-span-2">Date</div>
              <div className="col-span-2">Overall</div>
              <div className="col-span-3">Quadrant</div>
              <div className="col-span-1 text-right">Open</div>
            </div>
            {items.map((a) => (
              <Link
                key={a.id}
                to={`/results/${a.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-b border-slate-100 hover:bg-accent/40 transition-colors items-center"
                data-testid={`assessment-row-${a.id}`}
              >
                <div className="md:col-span-4 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{a.user?.name || "(unknown)"}</p>
                  <p className="text-xs text-slate-500 truncate">{a.user?.email || "—"}</p>
                </div>
                <div className="md:col-span-2 text-xs text-slate-600">{fmtDate(a.created_at)}</div>
                <div className="md:col-span-2 text-sm text-slate-800">
                  <span className="font-semibold">{a.overall_percent}%</span>
                  <span className="ml-2 text-xs text-slate-500">{a.overall_level}</span>
                </div>
                <div className="md:col-span-3 text-sm text-slate-700 truncate">{a.quadrant_name}</div>
                <div className="md:col-span-1 text-right text-primary">
                  <ArrowRight className="h-4 w-4 ml-auto" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
